import globalMethods from "@/globalMethods";
import {URLS} from "@/constants";
import {notify} from "@/plugins/notification";
import i18n from "@/i18n";


export default {
    namespaced: true,
    state: {
        invoices: [],
        shopRequisites: {},
        outstandingTotal: "0",
        totalCount: 0,
        hasMore: false,
        lastFilters: null,
    },
    mutations: {
        setInvoices(state, {invoices, outstandingTotal, totalCount, hasMore, filters}) {
            state.invoices = invoices;
            state.outstandingTotal = outstandingTotal;
            state.totalCount = totalCount;
            state.hasMore = hasMore;
            if (filters) state.lastFilters = filters;
        },
        appendInvoices(state, {invoices, outstandingTotal, totalCount, hasMore}) {
            state.invoices = [...state.invoices, ...invoices];
            state.outstandingTotal = outstandingTotal;
            state.totalCount = totalCount;
            state.hasMore = hasMore;
        },
        addInvoice(state, invoice) {
            state.invoices.unshift(invoice);
        },
        updateInvoice(state, updatedInvoice) {
            const index = state.invoices.findIndex(inv => inv.id === updatedInvoice.id);
            if (index !== -1) {
                state.invoices[index] = updatedInvoice;
            }
        },
        setShopRequisites(state, requisites) {
            state.shopRequisites = requisites;
        },
    },
    actions: {
        async getInvoices({commit, rootState}, params) {
            const {personalData, company} = rootState;
            const {dateFrom, dateTo, offset = 0, append = false, search = ""} = params;
            let url;
            if (search) {
                url = `${URLS.MODEL.INVOICE.INVOICES_URL}?search=${encodeURIComponent(search)}&limit=30`;
            } else {
                url = `${URLS.MODEL.INVOICE.INVOICES_URL}?date-from=${dateFrom}&date-to=${dateTo}&limit=30`;
            }
            if (offset > 0) {
                url += `&offset=${offset}`;
            }
            const options = {
                method: "GET",
                headers: {
                    role: personalData.role,
                    companyId: company.companyData?.token,
                    shopId: globalMethods.getDataFromLocalStorage("currentCashRegisterId"),
                },
                credentials: 'include',
            };
            const successfulHandler = (data) => {
                const payload = {
                    invoices: data.data,
                    outstandingTotal: data.outstanding_total || "0",
                    totalCount: data.total_count || 0,
                    hasMore: data.has_more || false,
                    filters: !append && !search ? {dateFrom, dateTo} : null,
                };
                if (append) {
                    commit("appendInvoices", payload);
                } else {
                    commit("setInvoices", payload);
                }
                return {successfully: true};
            };
            return globalMethods.sendRequestToGetJson({url, options, successfulHandler});
        },

        async createInvoice({commit, rootState}, params) {
            const {personalData, company, cashRegister} = rootState;
            const url = URLS.MODEL.INVOICE.INVOICES_URL;
            const options = {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRFToken": globalMethods.getCookie("csrftoken"),
                    role: personalData.role,
                    companyId: company.companyData?.token,
                    shopId: globalMethods.getDataFromLocalStorage("currentCashRegisterId"),
                    timezone: cashRegister.cashRegisterData?.timezone,
                },
                body: JSON.stringify(params),
                credentials: 'include',
            };
            const successfulHandler = (data) => {
                commit("addInvoice", data.data);
                notify(i18n.global.t("Invoice created successfully"));
                return {successfully: true, data: data.data};
            };
            const errorHandler = (data) => {
                return {successfully: false, detail: data};
            };
            return globalMethods.sendRequestToGetJson({url, options, successfulHandler, errorHandler});
        },

        async updateInvoice({commit, rootState}, params) {
            const {personalData, company} = rootState;
            const {invoiceId, ...body} = params;
            const url = URLS.MODEL.INVOICE.INVOICE_URL.replace("<ID>", invoiceId);
            const options = {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRFToken": globalMethods.getCookie("csrftoken"),
                    role: personalData.role,
                    companyId: company.companyData?.token,
                    shopId: globalMethods.getDataFromLocalStorage("currentCashRegisterId"),
                },
                body: JSON.stringify(body),
                credentials: 'include',
            };
            const successfulHandler = (data) => {
                commit("updateInvoice", data.data);
                notify(i18n.global.t("Invoice updated successfully"));
                return {successfully: true, data: data.data};
            };
            return globalMethods.sendRequestToGetJson({url, options, successfulHandler});
        },

        async deleteInvoice({commit, rootState}, invoiceId) {
            const {personalData, company} = rootState;
            const url = URLS.MODEL.INVOICE.INVOICE_URL.replace("<ID>", invoiceId);
            const options = {
                method: "DELETE",
                headers: {
                    "X-CSRFToken": globalMethods.getCookie("csrftoken"),
                    role: personalData.role,
                    companyId: company.companyData?.token,
                    shopId: globalMethods.getDataFromLocalStorage("currentCashRegisterId"),
                },
                credentials: 'include',
            };
            const successfulHandler = (data) => {
                commit("updateInvoice", data.data);
                notify(i18n.global.t("Invoice deleted"));
                return {successfully: true};
            };
            return globalMethods.sendRequestToGetJson({url, options, successfulHandler});
        },

        async downloadInvoicePdf({rootState}, invoiceId) {
            const {personalData, company} = rootState;
            const url = URLS.MODEL.INVOICE.INVOICE_URL.replace("<ID>", invoiceId);
            const options = {
                method: "GET",
                headers: {
                    role: personalData.role,
                    companyId: company.companyData?.token,
                    shopId: globalMethods.getDataFromLocalStorage("currentCashRegisterId"),
                },
                credentials: 'include',
            };
            const successfulHandler = (blob) => {
                const blobUrl = window.URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = blobUrl;
                link.download = `invoice_${invoiceId}.pdf`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(blobUrl);
                return {successfully: true};
            };
            return globalMethods.sendRequestToGetBlob({url, options, successfulHandler});
        },

        async getInvoicePdfBlob({rootState}, invoiceId) {
            const {personalData, company} = rootState;
            const url = URLS.MODEL.INVOICE.INVOICE_URL.replace("<ID>", invoiceId);
            const options = {
                method: "GET",
                headers: {
                    role: personalData.role,
                    companyId: company.companyData?.token,
                    shopId: globalMethods.getDataFromLocalStorage("currentCashRegisterId"),
                },
                credentials: 'include',
            };
            const successfulHandler = (blob) => {
                return {successfully: true, blob};
            };
            return globalMethods.sendRequestToGetBlob({url, options, successfulHandler});
        },

        async openInvoicePdfInNewTab({rootState}, invoiceId) {
            const {personalData, company} = rootState;
            const url = URLS.MODEL.INVOICE.INVOICE_URL.replace("<ID>", invoiceId);
            const options = {
                method: "GET",
                headers: {
                    role: personalData.role,
                    companyId: company.companyData?.token,
                    shopId: globalMethods.getDataFromLocalStorage("currentCashRegisterId"),
                },
                credentials: 'include',
            };
            const successfulHandler = (blob) => {
                const blobUrl = window.URL.createObjectURL(blob);
                window.open(blobUrl, "_blank");
                return {successfully: true};
            };
            return globalMethods.sendRequestToGetBlob({url, options, successfulHandler});
        },

        async getRequisites({commit, rootState}) {
            const {personalData, company} = rootState;
            const url = URLS.MODEL.INVOICE.REQUISITES_URL;
            const options = {
                method: "GET",
                headers: {
                    role: personalData.role,
                    companyId: company.companyData?.token,
                    shopId: globalMethods.getDataFromLocalStorage("currentCashRegisterId"),
                },
                credentials: 'include',
            };
            const successfulHandler = (data) => {
                commit("setShopRequisites", data.data);
                return {successfully: true};
            };
            return globalMethods.sendRequestToGetJson({url, options, successfulHandler});
        },

        async saveRequisites({commit, rootState}, requisites) {
            const {personalData, company} = rootState;
            const url = URLS.MODEL.INVOICE.REQUISITES_URL;
            const options = {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRFToken": globalMethods.getCookie("csrftoken"),
                    role: personalData.role,
                    companyId: company.companyData?.token,
                    shopId: globalMethods.getDataFromLocalStorage("currentCashRegisterId"),
                },
                body: JSON.stringify(requisites),
                credentials: 'include',
            };
            const successfulHandler = (data) => {
                commit("setShopRequisites", data.data);
                notify(i18n.global.t("Requisites saved"));
                return {successfully: true};
            };
            return globalMethods.sendRequestToGetJson({url, options, successfulHandler});
        },

    },
};
